import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  LifeBuoy, Search, ChevronDown, Mail, AlertTriangle, User as UserIcon,
  Image as ImageIcon, Heart, MessageCircle, ShieldAlert, Lock, Crown,
  Sparkles, KeyRound, Bell, UserX, HelpCircle, ArrowLeft,
} from "lucide-react";

// --- FAQ content ------------------------------------------------------------
// Kept in English for now; the framework already reads i18n keys elsewhere,
// so translating this later is a matter of adding keys — no structural change.
const CATEGORIES = [
  { id: "profile", icon: UserIcon, title: "Creating & editing your profile", qs: [
    { q: "How do I create my profile?", a: "After signing up you'll be taken to Profile automatically. Add your age, city, a short bio, your HSV status, gender, and what you're looking for, then hit Save. Your profile is what other members see when you appear in Discover." },
    { q: "How do I edit my profile later?", a: "Open the Profile page from the top navigation. Change anything you'd like and click Save. Changes are live immediately." },
    { q: "Is my HSV status shown to everyone?", a: "It's shown on your profile card in Discover so matches are honest and consensual from the start — this is the whole reason HerpMates exists. If you'd rather not share it, leave the field blank and it won't be displayed." },
  ]},
  { id: "photos", icon: ImageIcon, title: "Uploading photos", qs: [
    { q: "How do I add a photo?", a: "On the Profile page, use the Upload photo button (or Change photo if one is already set). JPG, PNG and WebP are supported." },
    { q: "Why do photos help?", a: "Profiles with a warm, well-lit photo of your face get many more matches. Group shots and heavily filtered photos tend to under-perform." },
    { q: "Can I hide my photo?", a: "Photos are optional. If you leave the photo blank, your card will show your initials on a warm gradient." },
  ]},
  { id: "matching", icon: Heart, title: "Matching & Discover", qs: [
    { q: "How does matching work?", a: "On Discover, swipe right (or tap the heart) on people you like and left (or the X) on people you don't. When two people like each other, it's a match — the other person appears in your Matches tab and you can start chatting." },
    { q: "Why don't I see more profiles?", a: "The pool grows every day. If Discover feels empty, try widening your gender/interested-in preferences or your age range on the Profile page. Founding Members and Premium members can also use Advanced Filters." },
    { q: "How many likes do I get per day?", a: "Free members get 10 likes per day, which resets at midnight your local time. Founding Members and Premium members have unlimited likes." },
  ]},
  { id: "messaging", icon: MessageCircle, title: "Messaging", qs: [
    { q: "When can I message someone?", a: "You can message someone once you've matched. Head to Messages, pick the match, and say hi." },
    { q: "I'm not sure what to say. Any help?", a: "Every conversation has an AI icebreaker button — tap Suggest an icebreaker and we'll generate a warm, on-topic opener based on both profiles." },
    { q: "Can I send photos or attachments in chat?", a: "Not yet — messaging is text-only for now so conversations feel safe and paced. We'll add optional photo sharing later." },
  ]},
  { id: "safety", icon: ShieldAlert, title: "Blocking & reporting", qs: [
    { q: "How do I block or report someone?", a: "Open the person's conversation or profile and use Report a Problem (below) to tell us what happened. Include their name or user ID if you can. We review every report personally and take action fast — including removing the account when needed." },
    { q: "Will they know I reported them?", a: "No. Reports are 100% confidential — the other person is never notified." },
    { q: "What behaviour is not allowed?", a: "Harassment, hate, unsolicited explicit content, impersonation, spam, and anyone under 18. HerpMates is a warm, respectful community — anything that undermines that gets removed." },
  ]},
  { id: "privacy", icon: Lock, title: "Privacy", qs: [
    { q: "Who can see my profile?", a: "Only signed-in HerpMates members can see your profile card in Discover. Nothing about your account is indexed by search engines." },
    { q: "Can I post anonymously in Community?", a: "Yes. When writing a post, toggle Post anonymously — your name and photo will be hidden on that post and its comments." },
    { q: "Do you share my data with third parties?", a: "No. We don't sell or share personal data with advertisers or brokers. Payments are handled by Stripe (industry standard), and that's the only third party that touches your billing info." },
  ]},
  { id: "premium", icon: Crown, title: "Premium membership", qs: [
    { q: "What does Premium unlock?", a: "See who liked you, unlimited likes, advanced Discover filters (age range, HSV status, intent), and a monthly 30-minute Boost that floats your profile to the top." },
    { q: "How much does it cost?", a: "$19.99/month or $199/year (saves 17%). You can cancel any time from the Premium page." },
    { q: "Payments aren't opening right now — why?", a: "We're finalizing our payment setup — Premium plans will be available very soon. Everything else in HerpMates keeps working normally, and Founding Members already have Premium for life." },
  ]},
  { id: "founding", icon: Sparkles, title: "First 50 Premium-for-Life offer", qs: [
    { q: "What is the Founding Member offer?", a: "The first 50 people to register on HerpMates automatically become Founding Members and get Premium free for life — no charges, no credit card, no expiry. It's our thank-you to the folks who help this community find its feet." },
    { q: "Am I a Founding Member?", a: "Open the Premium page. If you're one of the first 50, you'll see a You're a Founding Member ✨ card at the top — no pricing plans below it." },
    { q: "How many spots are left?", a: "The Landing page shows a live counter at the top. Once we hit 50, the offer closes automatically and Premium reverts to the paid plans." },
    { q: "Can I lose Founding status?", a: "No. Once you're in, you're in — the flag never expires. The only way to lose it would be to delete your account." },
  ]},
  { id: "account", icon: KeyRound, title: "Account & password problems", qs: [
    { q: "I can't sign in — what should I do?", a: "Double-check that you're using the email you registered with, and that your password has at least 6 characters. If you signed up with Google, use the Continue with Google button rather than a password." },
    { q: "How do I change my password?", a: "Password reset is coming soon. In the meantime, use Contact Support below with your registered email and we'll reset it manually within 1–2 business days." },
    { q: "I never got the email verification link.", a: "Check your spam/promotions folder. If it's not there, open the yellow banner at the top of the app and click Send / re-send link. You can keep using HerpMates while the email arrives." },
  ]},
  { id: "notifications", icon: Bell, title: "Notifications", qs: [
    { q: "What notifications do I get?", a: "Right now the app shows in-app toasts when you match with someone or receive a message while HerpMates is open. Push/email notifications for new matches and messages are on the roadmap." },
    { q: "Can I turn notifications off?", a: "Yes — your browser's site-settings menu lets you disable notifications for HerpMates entirely. We don't send anything you didn't ask for." },
  ]},
  { id: "delete", icon: UserX, title: "Deleting your account", qs: [
    { q: "How do I delete my account?", a: "Use Contact Support below with the subject Delete my account. We'll remove your profile, photos, messages, and matches within 3 business days and send you a confirmation email. Founding Member status is not transferable." },
    { q: "What happens to my data?", a: "Everything tied to your account is deleted from our active database. Backups are rotated out within 30 days, after which no copy remains." },
  ]},
  { id: "general", icon: HelpCircle, title: "General app use", qs: [
    { q: "Is HerpMates free?", a: "Yes — creating an account, editing your profile, matching, messaging, and using the Community and Awareness sections are all free. Premium is optional and adds power features." },
    { q: "What devices does HerpMates work on?", a: "Any modern browser — desktop, tablet, and phone. There's no native app to download; just open the URL in your favourite browser." },
    { q: "Is HerpMates medical advice?", a: "No — the Awareness section is educational only. Please see the medical disclaimer at the bottom of that page, and speak with a licensed healthcare provider for anything specific to your health." },
  ]},
];

function matches(text, query) {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

// ---------------------------------------------------------------------------
export default function Help() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("faq"); // faq | contact | report

  const results = useMemo(() => {
    if (!query.trim()) return CATEGORIES;
    return CATEGORIES
      .map((c) => ({
        ...c,
        qs: c.qs.filter((qa) => matches(c.title, query) || matches(qa.q, query) || matches(qa.a, query)),
      }))
      .filter((c) => c.qs.length > 0);
  }, [query]);

  const totalHits = results.reduce((acc, c) => acc + c.qs.length, 0);
  const helpLabel = t("nav.help", { defaultValue: "Help" });

  return (
    <div className="min-h-screen bg-[#FFFDF9] dark:bg-[#1A1615]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="help-page">
        {/* Hero */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF0EC] text-[#C24C29] text-sm font-medium mb-4">
            <LifeBuoy className="w-4 h-4" /> {helpLabel}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight">
            How can we help?
          </h1>
          <p className="text-base text-[#6E655F] dark:text-[#B5AAA1] mt-3 max-w-xl mx-auto">
            Search the answers below. If none of them fit, contact our team or report a problem — we'll get back to you at the email on your account.
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex items-center gap-2 mb-6 justify-center flex-wrap">
          {[
            { id: "faq", label: "Answers", icon: HelpCircle },
            { id: "contact", label: "Contact Support", icon: Mail },
            { id: "report", label: "Report a Problem", icon: AlertTriangle },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`help-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === id
                  ? "bg-[#D95D39] text-white"
                  : "bg-white dark:bg-[#221E1C] text-[#2C2623] dark:text-[#FAF5F0] border border-[#E8DEC8] dark:border-[#423B36] hover:bg-[#F9F5EE] dark:hover:bg-[#2A2422]"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "faq" && (
          <>
            {/* Search */}
            <div className="relative mb-6" data-testid="help-search-wrap">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E655F] dark:text-[#B5AAA1]" />
              <Input
                data-testid="help-search-input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                className="h-12 pl-11 rounded-full bg-white dark:bg-[#221E1C] border-[#E8DEC8] dark:border-[#423B36]"
              />
              {query && (
                <div className="text-xs text-[#6E655F] dark:text-[#B5AAA1] mt-2 ml-4" data-testid="help-search-hits">
                  {totalHits === 0 ? "No results — try Contact Support below." : `${totalHits} result${totalHits === 1 ? "" : "s"}`}
                </div>
              )}
            </div>

            {/* Category cards */}
            <div className="space-y-4">
              {results.map(({ id, icon: Icon, title, qs }) => (
                <Card key={id} data-testid={`help-category-${id}`}
                  className="p-4 sm:p-6 rounded-2xl border-[#E8DEC8] dark:border-[#423B36] bg-white dark:bg-[#221E1C]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[#FFF0EC] dark:bg-[#3A211B] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#D95D39]" />
                    </div>
                    <h2 className="font-serif text-lg font-semibold text-[#2C2623] dark:text-[#FAF5F0]">
                      {title}
                    </h2>
                  </div>
                  <div className="divide-y divide-[#F0E6D2] dark:divide-[#3A322F]">
                    {qs.map((qa, idx) => {
                      const key = `${id}-${idx}`;
                      const open = openId === key;
                      return (
                        <div key={key}>
                          <button
                            type="button"
                            onClick={() => setOpenId(open ? null : key)}
                            data-testid={`help-question-${id}-${idx}`}
                            className="w-full text-left flex items-start justify-between gap-3 py-3 group"
                          >
                            <span className="text-[#2C2623] dark:text-[#FAF5F0] group-hover:text-[#C24C29] dark:group-hover:text-[#E07A5F] font-medium">
                              {qa.q}
                            </span>
                            <ChevronDown className={`w-4 h-4 flex-shrink-0 mt-1 text-[#6E655F] dark:text-[#B5AAA1] transition-transform ${open ? "rotate-180" : ""}`} />
                          </button>
                          {open && (
                            <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed pb-4 pr-6" data-testid={`help-answer-${id}-${idx}`}>
                              {qa.a}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
              {results.length === 0 && (
                <Card className="p-8 text-center rounded-2xl border-[#E8DEC8] dark:border-[#423B36] bg-white dark:bg-[#221E1C]" data-testid="help-no-results">
                  <p className="text-[#4A413B] dark:text-[#B5AAA1]">
                    Nothing matched "{query}". Try a shorter search — or use Contact Support below and we'll help you directly.
                  </p>
                  <Button
                    onClick={() => setTab("contact")}
                    className="mt-4 rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white">
                    Contact Support
                  </Button>
                </Card>
              )}
            </div>
          </>
        )}

        {tab === "contact" && <SupportForm kind="contact" defaultEmail={user?.email || ""} onBack={() => setTab("faq")} />}
        {tab === "report" && <SupportForm kind="report" defaultEmail={user?.email || ""} onBack={() => setTab("faq")} />}

        {/* Honest note — no fake AI, no fake 24/7 live support */}
        <p className="text-center text-xs text-[#6E655F] dark:text-[#B5AAA1] mt-10 max-w-lg mx-auto leading-relaxed" data-testid="help-honesty-note">
          HerpMates is built by a small team. We read every message personally and reply as fast as we can — usually within 1–2 business days. There's no live chat or 24/7 support (yet).
        </p>
      </main>
    </div>
  );
}

// --- Contact / Report form -------------------------------------------------
function SupportForm({ kind, defaultEmail, onBack }) {
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(kind === "report" ? "bug" : "general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const isReport = kind === "report";
  const title = isReport ? "Report a Problem" : "Contact Support";
  const description = isReport
    ? "Tell us what went wrong. Screenshots you attach in a follow-up email help a lot."
    : "Ask us anything — account, matches, Premium, anything at all. We read every message.";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/submit", {
        kind, email, subject, category,
        message,
      });
      setTicketId(data?.ticket_id || "received");
      toast.success(isReport ? "Report received — thanks for letting us know." : "Message received — we'll be in touch.");
      setSubject(""); setMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (ticketId) {
    return (
      <Card className="p-6 sm:p-8 rounded-3xl border-[#E8DEC8] dark:border-[#423B36] bg-white dark:bg-[#221E1C]" data-testid={`support-success-${kind}`}>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#DDEDE4] dark:bg-[#1F3E37] flex items-center justify-center mb-3">
            <Mail className="w-6 h-6 text-[#2A524A]" />
          </div>
          <h3 className="font-serif text-2xl font-semibold text-[#2C2623] dark:text-[#FAF5F0] mb-2">
            Thank you — we've got it.
          </h3>
          <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed max-w-md mx-auto">
            Your ticket <span className="font-mono text-xs bg-[#F9F5EE] dark:bg-[#2A2422] px-2 py-0.5 rounded" data-testid="support-ticket-id">{ticketId}</span> is with our team.
            We'll reply to <b className="text-[#2C2623] dark:text-[#FAF5F0]">{email}</b> within 1–2 business days.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" onClick={onBack} data-testid="support-back-btn"
              className="rounded-full border-[#E8DEC8] dark:border-[#423B36]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Answers
            </Button>
            <Button onClick={() => { setTicketId(null); }} data-testid="support-new-ticket-btn"
              className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">
              Send another
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 sm:p-8 rounded-3xl border-[#E8DEC8] dark:border-[#423B36] bg-white dark:bg-[#221E1C]" data-testid={`support-form-${kind}`}>
      <div className="mb-5">
        <div className="inline-flex items-center gap-2 text-[#B45309] text-xs uppercase tracking-widest font-semibold mb-2">
          {isReport ? <AlertTriangle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
          {isReport ? "Bug / issue" : "Get in touch"}
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C2623] dark:text-[#FAF5F0]">{title}</h2>
        <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-2">{description}</p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="s-email" className="text-[#2C2623] dark:text-[#FAF5F0]">Your email</Label>
          <Input id="s-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="support-email-input"
            placeholder="you@example.com" className="h-11" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="s-cat" className="text-[#2C2623] dark:text-[#FAF5F0]">Topic</Label>
            <select
              id="s-cat" value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="support-category-select"
              className="h-11 w-full rounded-md border border-[#E8DEC8] dark:border-[#423B36] bg-white dark:bg-[#221E1C] text-sm px-3">
              {(isReport
                ? ["bug", "profile", "matching", "messaging", "premium", "safety", "other"]
                : ["general", "account", "profile", "matching", "messaging", "premium", "founding", "delete", "other"]
              ).map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-subj" className="text-[#2C2623] dark:text-[#FAF5F0]">Subject <span className="text-[#6E655F]">(optional)</span></Label>
            <Input id="s-subj" value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="support-subject-input"
              placeholder={isReport ? "Discover crashes on iPhone" : "How do I…"} className="h-11" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="s-msg" className="text-[#2C2623] dark:text-[#FAF5F0]">Your message</Label>
          <Textarea id="s-msg" required minLength={5} rows={6} value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="support-message-input"
            placeholder={isReport
              ? "What did you try? What happened? What did you expect to happen?"
              : "Tell us what's going on — we're listening."} />
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onBack} data-testid="support-cancel-btn"
            className="rounded-full border-[#E8DEC8] dark:border-[#423B36]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button type="submit" disabled={busy} data-testid="support-submit-btn"
            className="rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white font-semibold px-6">
            {busy ? "Sending…" : (isReport ? "Send report" : "Send message")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
